import { pragueParts, addDays, instantFromPragueWall, minutesToHhmm } from '../../../shared/time.js'
import { isCzechHoliday, czechHolidayName } from '../../../shared/holidays.js'
import { IconChevron } from './Icons.jsx'
import { pragueDateLabel } from '../lib/i18n.js'

/**
 * Pick ANY Prague wall-clock instant — day and time — not just a minute of today.
 * Everything is derived from `value`, so there is no second copy of the state to
 * drift; each control just resolves a new instant.
 */
const pad = (n) => String(n).padStart(2, '0')
const iso = (p) => `${p.year}-${pad(p.month)}-${pad(p.day)}`
const TIME_PRESETS = [480, 1080, 1320] // 08:00, 18:00, 22:00

export default function WhenPicker({ t, lang, value, isNow, now, onChange }) {
  const p = pragueParts(value)
  const holiday = isCzechHoliday(p.year, p.month, p.day)

  const at = (ymd, minutes) => instantFromPragueWall(ymd.year, ymd.month, ymd.day, minutes)
  const shiftDay = (delta) => onChange(at(addDays(p, delta), p.minutesOfDay))
  const setMinutes = (m) => onChange(at(p, m))

  const nowParts = pragueParts(now)

  // Comparing the calendar day to today's keeps "Dnes/Zítra" honest across midnight.
  const dayDelta = Math.round(
    (Date.UTC(p.year, p.month - 1, p.day) - Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day)) / 86400000
  )
  const dayName = dayDelta === 0 ? t('today') : dayDelta === 1 ? t('tomorrow') : pragueDateLabel(value, lang)

  // next Saturday, or today if it already is one
  const toSaturday = (6 - nowParts.isoWeekday + 7) % 7

  // A preset lights up when the value it would set is the value you already
  // have — derived from `value`, never a second copy of "which one I pressed".
  const isAt = (delta, minutes) => !isNow && dayDelta === delta && p.minutesOfDay === minutes

  const presets = [
    { key: 'now', label: t('now'), active: isNow, apply: () => onChange(null) },
    {
      key: 'plus1',
      label: t('inAnHour'),
      // Relative, so it drifts off an exact match as the clock ticks: keep it lit
      // while the pick is still about an hour out.
      active: !isNow && Math.abs(value.getTime() - (now.getTime() + 3600000)) < 3 * 60 * 1000,
      apply: () => onChange(new Date(now.getTime() + 60 * 60 * 1000)),
    },
    { key: 'tonight', label: t('tonight'), active: isAt(0, 20 * 60), apply: () => onChange(at(nowParts, 20 * 60)) },
    {
      key: 'tomorrow',
      label: t('tomorrowMorning'),
      active: isAt(1, 8 * 60),
      apply: () => onChange(at(addDays(nowParts, 1), 8 * 60)),
    },
    {
      key: 'weekend',
      label: t('weekend'),
      active: isAt(toSaturday, 10 * 60),
      apply: () => onChange(at(addDays(nowParts, toSaturday), 10 * 60)),
    },
  ]

  return (
    <div className="picker">
      <div className="chips chips--wrap" role="group" aria-label={t('planWhenTitle')}>
        {presets.map((x) => (
          <button key={x.key} type="button" className={`chip ${x.active ? 'is-active' : ''}`} aria-pressed={x.active} onClick={x.apply}>
            {x.label}
          </button>
        ))}
      </div>

      {/* Day stepper — any date, either direction; tapping the date opens the
          native picker, because "any day" must not mean 161 taps to Christmas. */}
      <div className="daypick">
        <button type="button" className="daypick__step" onClick={() => shiftDay(-1)} aria-label={t('prevDay')}>
          <IconChevron className="is-flipped" />
        </button>
        <label className="daypick__label">
          <span className="daypick__day">{dayName}</span>
          <span className="daypick__date">
            {p.day}. {p.month}. {p.year}
          </span>
          <input
            className="daypick__input"
            type="date"
            value={iso(p)}
            onChange={(e) => {
              const [y, m, d] = e.target.value.split('-').map(Number)
              if (y) onChange(at({ year: y, month: m, day: d }, p.minutesOfDay))
            }}
            aria-label={t('planWhenTitle')}
          />
        </label>
        <button type="button" className="daypick__step" onClick={() => shiftDay(1)} aria-label={t('nextDay')}>
          <IconChevron />
        </button>
      </div>

      {holiday && <p className="note note--warn">{t('holidayPickNote', czechHolidayName(p.year, p.month, p.day))}</p>}

      <div className="slider-field">
        <div className="slider-field__head">
          <span className="section__title">{t('time')}</span>
          <output className={`pill ${isNow ? '' : 'is-accent'}`}>{minutesToHhmm(p.minutesOfDay)}</output>
        </div>
        <input
          className="slider"
          style={{ '--pct': `${(p.minutesOfDay / 1439) * 100}%` }}
          type="range"
          min="0"
          max="1439"
          step="5"
          value={p.minutesOfDay}
          onChange={(e) => setMinutes(Number(e.target.value))}
          aria-label={t('time')}
          aria-valuetext={minutesToHhmm(p.minutesOfDay)}
        />
        <div className="chips" role="group" aria-label={t('time')}>
          {TIME_PRESETS.map((m) => {
            const active = !isNow && p.minutesOfDay === m
            return (
              <button key={m} type="button" className={`chip chip--fill ${active ? 'is-active' : ''}`} aria-pressed={active} onClick={() => setMinutes(m)}>
                {minutesToHhmm(m)}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
